import React from "react";
import { Table, Dropdown, Spin, Popover, Modal } from "antd";
import { EllipsisOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
    IValueBoolean,
    TMediaPlanConfig,
    TMediaPlanList,
    TPlanList,
} from "@/ts/common-types";
import { GENERATE_MEDIA_PLAN_CONFIG, MONTH_OPTIONS } from "@/ts/constants";
import { disableMediaPlan, internationalizeNumericValue } from "@/ts/utils";
import dayjs from "dayjs";
import { setSelectedMediaPlanData } from "@/features/selectedDataSlice";
import { setIsGenerateMediaPlanLoading } from "@/features/loaderSlice";
import {
    setMediaPlanConfigValues,
    updateShowNewMediaPlanDisplay,
} from "@/features/modalSlice";
import { filter, map, mapObject } from "underscore";
import {
    setGenerateMediaPlanData,
    setIsMediaPlanEdit,
    setMediaPlanBudget,
    setMediaPlanningList,
} from "@/features/gridDataSlice";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { getPlanTrackingData } from "@/ts/retrieve-data";
import {
    EditOutlined,
    BarChartOutlined,
    DeleteOutlined,
} from "@ant-design/icons";

const ListAllPlans: React.FC<TPlanList> = ({ rowData }) => {
    const dispatch = useDispatch();

    const mediaPlanListLoading = useSelector(
        (state: { mediaPlanListLoading: IValueBoolean }) =>
            state.mediaPlanListLoading.value
    );

    const handlePlanEdit = (planRecord: TMediaPlanList) => {
        const {
            plan_id,
            plan_budget,
            plan_levels,
            plan_month,
            plan_name,
            plan_details,
            latest_plan_version,
        } = planRecord;

        dispatch(setIsMediaPlanEdit(true));
        dispatch(setIsGenerateMediaPlanLoading(true));
        dispatch(updateShowNewMediaPlanDisplay(true));
        dispatch(setMediaPlanBudget(plan_budget));
        dispatch(
            setMediaPlanConfigValues({
                plan_id,
                plan_name,
                plan_level: plan_levels,
                plan_budget: plan_budget.toString(),
                plan_month,
                plan_version: +latest_plan_version,
            })
        );

        const updatedPlanData = mapObject(
            plan_details,
            (planItems, planLevel) => {
                const recordKey =
                    GENERATE_MEDIA_PLAN_CONFIG[planLevel]?.fieldColumn ||
                    GENERATE_MEDIA_PLAN_CONFIG["Default"].fieldColumn;
                return map(planItems, (item) => ({
                    ...item,
                    [recordKey]: item.recordKey,
                }));
            }
        );

        dispatch(setGenerateMediaPlanData(updatedPlanData));

        setSelectedMediaPlanData(planRecord);
        dispatch(setIsGenerateMediaPlanLoading(false));
    };

    const showDeleteConfirm = async (planId: number) => {
        Modal.confirm({
            title: "Are you sure you want to delete this plan?",
            icon: <ExclamationCircleOutlined />,
            content: "This action cannot be undone.",
            okText: "Yes, Delete",
            okType: "danger",
            cancelText: "Cancel",
            className: "plan-confirm-modal",
            style: { position: "relative", top: "250px" },
            onOk: async () => await handlePlanDelete(planId),
        });
    };

    const handlePlanDelete = async (planId: number) => {
        try {
            const isPlanDeleted = await disableMediaPlan(planId);
            if (isPlanDeleted) {
                const updatedMediaPlanList = filter(
                    rowData,
                    (plan) => plan.plan_id !== planId
                );
                dispatch(setMediaPlanningList(updatedMediaPlanList));
            }
        } catch (err: any) {
            console.error(err);
        }
    };

    const handlePlanTracking = async (planRecord: TMediaPlanList) => {
        try {
            const {
                plan_id,
                plan_month,
                plan_year,
                plan_levels,
                plan_budget,
                plan_name,
            } = planRecord;

            dispatch(setIsGenerateMediaPlanLoading(true));
            dispatch(
                setMediaPlanConfigValues({
                    plan_name,
                    plan_budget: plan_budget.toString(),
                    plan_month,
                    plan_level: plan_levels,
                } as TMediaPlanConfig)
            );
            dispatch(updateShowNewMediaPlanDisplay(true));
            dispatch(setMediaPlanBudget(+plan_budget));
            const planTrackingConfig = {
                plan_id,
                plan_month,
                plan_year,
                plan_levels,
            };
            const trackedPlanData = await getPlanTrackingData(
                planTrackingConfig
            );
            dispatch(setGenerateMediaPlanData(trackedPlanData));
            dispatch(setIsGenerateMediaPlanLoading(false));
        } catch (err: any) {
            console.error(err);
        }
    };

    const columns = [
        {
            title: "PLAN NAME",
            dataIndex: "plan_name",
            key: "plan_name",
        },
        {
            title: "RECORD TYPE",
            dataIndex: "plan_levels",
            key: "plan_levels",
            render: (planLevels: string[]) => {
                const firstLevel = planLevels[0];
                const remainingCount = planLevels.length - 1;
                const popoverContent = (
                    <div className="p-3">
                        {planLevels.map((level, index) => (
                            <div key={index}>
                                <b>{level}</b>
                            </div>
                        ))}
                    </div>
                );

                return (
                    <Popover content={popoverContent} trigger="hover">
                        <span>
                            {firstLevel} {remainingCount > 0 ? `...` : ""}
                        </span>
                    </Popover>
                );
            },
        },
        {
            title: "BUDGET",
            dataIndex: "plan_budget",
            key: "plan_budget",
            render: (budget: number) => internationalizeNumericValue(budget),
        },
        {
            title: "MONTH",
            dataIndex: "plan_month",
            key: "plan_month",
            render: (month: number, planRecord: TMediaPlanList) =>
                `${MONTH_OPTIONS[month - 1].label} ${planRecord.plan_year
                    .toString()
                    .slice(-2)}'`,
        },
        {
            title: "CREATED BY",
            dataIndex: "created_by",
            key: "created_by",
        },
        {
            title: "CREATED ON",
            dataIndex: "created_at",
            key: "created_at",
            render: (createdAt: string) =>
                dayjs(createdAt).format("Do MMM, YY"),
        },
        {
            title: "ACTIONS",
            key: "actions",
            render: (text: string, planRecord: TMediaPlanList) => (
                <Dropdown
                    menu={{
                        items: [
                            {
                                key: "edit",
                                label: (
                                    <>
                                        <EditOutlined className="me-1 my-1" />{" "}
                                        Edit
                                    </>
                                ),
                                onClick: () => handlePlanEdit(planRecord),
                            },
                            {
                                key: "track",
                                label: (
                                    <>
                                        <BarChartOutlined className="me-1 my-2" />{" "}
                                        Track
                                    </>
                                ),
                                onClick: () => handlePlanTracking(planRecord),
                            },
                            {
                                key: "delete",
                                label: (
                                    <>
                                        <DeleteOutlined className="me-1 my-1 text-danger" />{" "}
                                        Delete
                                    </>
                                ),
                                danger: true,
                                onClick: () =>
                                    showDeleteConfirm(planRecord.plan_id),
                            },
                        ],
                    }}
                    trigger={["click"]}
                >
                    <EllipsisOutlined
                        style={{ fontSize: 20, cursor: "pointer" }}
                    />
                </Dropdown>
            ),
        },
    ];

    return (
        <>
            {mediaPlanListLoading ? (
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "70vh",
                    }}
                >
                    <Spin tip="Loading" size="large"></Spin>
                </div>
            ) : (
                <Table
                        dataSource={rowData}
                    columns={columns}
                    pagination={false}
                />
            )}
        </>
    );
};

export default ListAllPlans;
